import { type CharacterDraftTransfer } from "./character-editor.js";
import { Emulator } from "./emulator.js";
import { createHistoryManager, type HistoryManager } from "./tile-map-history.js";
import { rasterizeFloodFill, rasterizeRectangleOutline, type PaintPoint } from "./tile-map-paint-tools.js";
import { createWorkbenchMenuBar } from "./workbench-menu-bar.js";
import type { WorkbenchMenu, WorkbenchMenuBarController } from "./workbench-menu-types.js";
import { actionMenuItem, buttonMenuItem, menuSeparator, selectOptionMenuItem } from "./workbench-menu-utils.js";

const SCREEN_SIZE = 1000;
const COLOR_SIZE = 500;
const CELL_COLUMNS = 40;
const CELL_ROWS = 25;
const CELL_DIMENSION = 8;
const PREVIEW_WIDTH = CELL_COLUMNS * CELL_DIMENSION;
const PREVIEW_HEIGHT = CELL_ROWS * CELL_DIMENSION;
const GLYPH_STRIDE = 8;
const GLYPHS_PER_BLOCK = 128;
const ATLAS_COLUMNS = 16;
const ATLAS_ROWS = 8;
const ATLAS_CELL_SIZE = 24;
const ATLAS_GLYPH_SIZE = 16;
const ATLAS_GLYPH_OFFSET = (ATLAS_CELL_SIZE - ATLAS_GLYPH_SIZE) / 2;
const PALETTE_SIZE = 16;
const TEXT_BIT7_INTERNAL_MASK = 0x20;
const VOC_CTRL1_BMM_MASK = 0x20;
const VOC_CTRL2_MCM_MASK = 0x10;
const VOC_BORDER_COLOR_ADDR = 0xd020;
const VOC_BG_COLOR_ADDR = 0xd021;

type TileMapGlyphMode = "256-direct" | "128-reverse";
type TileStyleBrushMode = "tile-only" | "tile-current-color" | "tile-style";
type TileMapTool = "stamp" | "fill" | "rectangle";
type TileMapZoom = 2 | 4 | 8;
type CharsetBacking = "rom" | "ram" | "draft";

type TileMapCharset = {
  backing: CharsetBacking;
  bytes: Uint8Array;
  glyphCount: 128 | 256;
  sourceLabel: string;
};

type TileStyle = {
  category: string | null;
  fgColor: number;
  glyphIndex: number;
  hotkey: number | null;
  id: string;
  name: string;
  reverse: boolean;
};

type TileMapDraft = {
  activeBlock: number;
  bgColor: number;
  borderColor: number;
  charset: TileMapCharset;
  color: Uint8Array;
  dirty: boolean;
  glyphMode: TileMapGlyphMode;
  palette: Uint16Array;
  screen: Uint8Array;
  selectedCellX: number;
  selectedCellY: number;
  selectedColor: number;
  selectedReverse: boolean;
  selectedTile: number;
  sourceLabel: string;
  styleCounter: number;
  textBit7Internal: boolean;
  tileStyles: TileStyle[];
  zoom: TileMapZoom;
};

type ActiveTextTarget = {
  bank: number;
  bankBase: number;
  charBase: number;
  charOffset: number;
  colorBase: number;
  glyphCount: 128 | 256;
  glyphMode: TileMapGlyphMode;
  screenBase: number;
  textBit7Internal: boolean;
  tilesetBacking: CharsetBacking;
  tilesetLabel: string;
};

type TileMapProjectFileV1 = {
  bgColor: number;
  borderColor: number;
  charsetBytes: number[];
  charsetGlyphCount: 128 | 256;
  color: number[];
  format: "am8-tilemap-project";
  glyphMode: TileMapGlyphMode;
  height: 25;
  palette: number[];
  screen: number[];
  textBit7Internal: boolean;
  version: 1;
  width: 40;
};

type TileMapProjectTileStyleV2 = {
  category?: string | null;
  fgColor: number;
  glyphIndex: number;
  hotkey?: number | null;
  id: string;
  name: string;
  reverse: boolean;
};

type TileMapProjectFileV2 = Omit<TileMapProjectFileV1, "version"> & {
  tileStyles?: TileMapProjectTileStyleV2[];
  version: 2;
};

type TileMapProjectFile = TileMapProjectFileV1 | TileMapProjectFileV2;

type TileMapEditorInterop = {
  getCharacterDraft(): CharacterDraftTransfer | null;
  openTilesetInCharacterEditor(draft: CharacterDraftTransfer): boolean;
};

type SuggestedTileStyle = {
  count: number;
  fgColor: number;
  glyphIndex: number;
  reverse: boolean;
  signature: string;
};

type ResolvedBrush = {
  fgColor: number;
  screenCode: number;
  writesColor: boolean;
};

type ToolButtonView = {
  button: HTMLButtonElement;
  value: TileMapTool;
};

export type TileMapEditorElements = {
  applyStyleToMatchingTrigger: HTMLButtonElement;
  applyStyleToSelectionTrigger: HTMLButtonElement;
  atlasCanvas: HTMLCanvasElement;
  bankMeta: HTMLElement;
  bgColorSelect: HTMLSelectElement;
  blockSelect: HTMLSelectElement;
  borderColorSelect: HTMLSelectElement;
  brushModeSelect: HTMLSelectElement;
  byteOutput: HTMLTextAreaElement;
  colorBaseMeta: HTMLElement;
  createStyleFromCellTrigger: HTMLButtonElement;
  dirtyIndicator: HTMLElement;
  editInCharacterEditorTrigger: HTMLButtonElement;
  editorCanvas: HTMLCanvasElement;
  editorScroller: HTMLDivElement;
  exportByteTrigger: HTMLButtonElement;
  exportProjectTrigger: HTMLButtonElement;
  fgColorSelect: HTMLSelectElement;
  historyStatus: HTMLParagraphElement;
  importProjectInput: HTMLInputElement;
  importProjectTrigger: HTMLButtonElement;
  layout: HTMLDivElement;
  leftRail: HTMLButtonElement;
  modeMeta: HTMLElement;
  newBlankTrigger: HTMLButtonElement;
  panel: HTMLElement;
  previewCanvas: HTMLCanvasElement;
  readActiveTrigger: HTMLButtonElement;
  redoTrigger: HTMLButtonElement;
  reverseToggle: HTMLInputElement;
  rightRail: HTMLButtonElement;
  screenBaseMeta: HTMLElement;
  selectedCellMeta: HTMLElement;
  selectedColorMeta: HTMLElement;
  selectedScreenCodeMeta: HTMLElement;
  selectedTileMeta: HTMLElement;
  quickStyles: HTMLDivElement;
  saveStyleTrigger: HTMLButtonElement;
  sourceMeta: HTMLElement;
  status: HTMLParagraphElement;
  styleList: HTMLDivElement;
  styleSuggestions: HTMLDivElement;
  suggestStylesTrigger: HTMLButtonElement;
  tilesetSourceMeta: HTMLElement;
  toolButtons: HTMLDivElement;
  undoTrigger: HTMLButtonElement;
  useCharacterDraftTrigger: HTMLButtonElement;
  writeAllTrigger: HTMLButtonElement;
  writeColorTrigger: HTMLButtonElement;
  writeHint: HTMLParagraphElement;
  writeScreenTrigger: HTMLButtonElement;
  zoomSelect: HTMLSelectElement;
};

export type TileMapEditorController = {
  refresh(): void;
};

function formatHexByte(value: number): string {
  return (value & 0xff).toString(16).toUpperCase().padStart(2, "0");
}

function formatHexWord(value: number): string {
  return (value & 0xffff).toString(16).toUpperCase().padStart(4, "0");
}

function formatHexBytePrefixed(value: number): string {
  return `0x${formatHexByte(value)}`;
}

function formatHexWordPrefixed(value: number): string {
  return `0x${formatHexWord(value)}`;
}

function formatAsmByte(value: number): string {
  return `$${formatHexByte(value)}`;
}

function glyphModeLabel(mode: TileMapGlyphMode): string {
  return mode === "128-reverse" ? "128 glyphs + reverse via Bit7" : "256 direct glyph codes";
}

function toolLabel(tool: TileMapTool): string {
  if (tool === "fill") {
    return "Fill";
  }
  if (tool === "rectangle") {
    return "Rectangle";
  }
  return "Stamp";
}

function cloneBytes(data: Uint8Array): Uint8Array {
  const copy = new Uint8Array(data.byteLength);
  copy.set(data);
  return copy;
}

function clonePalette(data: Uint16Array): Uint16Array {
  const copy = new Uint16Array(data.length);
  copy.set(data);
  return copy;
}

function cloneCharset(charset: TileMapCharset): TileMapCharset {
  return {
    backing: charset.backing,
    bytes: cloneBytes(charset.bytes),
    glyphCount: charset.glyphCount,
    sourceLabel: charset.sourceLabel
  };
}

function cloneTileStyle(style: TileStyle): TileStyle {
  return {
    category: style.category,
    fgColor: style.fgColor,
    glyphIndex: style.glyphIndex,
    hotkey: style.hotkey,
    id: style.id,
    name: style.name,
    reverse: style.reverse
  };
}

function cloneDraft(draft: TileMapDraft): TileMapDraft {
  return {
    activeBlock: draft.activeBlock,
    bgColor: draft.bgColor,
    borderColor: draft.borderColor,
    charset: cloneCharset(draft.charset),
    color: cloneBytes(draft.color),
    dirty: draft.dirty,
    glyphMode: draft.glyphMode,
    palette: clonePalette(draft.palette),
    screen: cloneBytes(draft.screen),
    selectedCellX: draft.selectedCellX,
    selectedCellY: draft.selectedCellY,
    selectedColor: draft.selectedColor,
    selectedReverse: draft.selectedReverse,
    selectedTile: draft.selectedTile,
    sourceLabel: draft.sourceLabel,
    styleCounter: draft.styleCounter,
    textBit7Internal: draft.textBit7Internal,
    tileStyles: draft.tileStyles.map(cloneTileStyle),
    zoom: draft.zoom
  };
}

function validateByteArray(name: string, value: unknown, length: number): Uint8Array {
  if (!Array.isArray(value) || value.length !== length) {
    throw new Error(`${name} must contain exactly ${length} byte values.`);
  }
  const bytes = new Uint8Array(length);
  for (let index = 0; index < length; index += 1) {
    const item = value[index];
    if (!Number.isInteger(item) || item < 0 || item > 0xff) {
      throw new Error(`${name}[${index}] must be an integer in range 0..255.`);
    }
    bytes[index] = item;
  }
  return bytes;
}

function validatePaletteArray(value: unknown): Uint16Array {
  if (!Array.isArray(value) || value.length !== PALETTE_SIZE) {
    throw new Error(`palette must contain exactly ${PALETTE_SIZE} RGB444 values.`);
  }
  const palette = new Uint16Array(PALETTE_SIZE);
  for (let index = 0; index < PALETTE_SIZE; index += 1) {
    const item = value[index];
    if (!Number.isInteger(item) || item < 0 || item > 0x0fff) {
      throw new Error(`palette[${index}] must be an integer in range 0..4095.`);
    }
    palette[index] = item;
  }
  return palette;
}

function validateStyleArray(value: unknown, glyphCount: 128 | 256): TileStyle[] {
  if (value === undefined) {
    return [];
  }
  if (!Array.isArray(value)) {
    throw new Error("tileStyles must be an array when present.");
  }

  const styles: TileStyle[] = [];
  const seenIds = new Set<string>();
  const seenHotkeys = new Set<number>();
  for (let index = 0; index < value.length; index += 1) {
    const entry = value[index];
    if (typeof entry !== "object" || entry === null) {
      throw new Error(`tileStyles[${index}] must be an object.`);
    }
    const raw = entry as Partial<TileMapProjectTileStyleV2>;
    if (typeof raw.id !== "string" || raw.id.trim() === "") {
      throw new Error(`tileStyles[${index}].id must be a non-empty string.`);
    }
    if (seenIds.has(raw.id)) {
      throw new Error(`tileStyles[${index}].id must be unique within the project.`);
    }
    seenIds.add(raw.id);
    if (typeof raw.name !== "string" || raw.name.trim() === "") {
      throw new Error(`tileStyles[${index}].name must be a non-empty string.`);
    }
    const glyphIndex = raw.glyphIndex;
    if (typeof glyphIndex !== "number" || !Number.isInteger(glyphIndex) || glyphIndex < 0 || glyphIndex >= glyphCount) {
      throw new Error(`tileStyles[${index}].glyphIndex must be an integer in range 0..${glyphCount - 1}.`);
    }
    const fgColor = raw.fgColor;
    if (typeof fgColor !== "number" || !Number.isInteger(fgColor) || fgColor < 0 || fgColor > 0x0f) {
      throw new Error(`tileStyles[${index}].fgColor must be an integer in range 0..15.`);
    }
    const reverse = raw.reverse;
    if (typeof reverse !== "boolean") {
      throw new Error(`tileStyles[${index}].reverse must be a boolean.`);
    }
    if (raw.category !== undefined && raw.category !== null && typeof raw.category !== "string") {
      throw new Error(`tileStyles[${index}].category must be null or a string.`);
    }
    if (raw.hotkey !== undefined && raw.hotkey !== null && (!Number.isInteger(raw.hotkey) || raw.hotkey < 1 || raw.hotkey > 9)) {
      throw new Error(`tileStyles[${index}].hotkey must be null or an integer in range 1..9.`);
    }
    if (raw.hotkey !== undefined && raw.hotkey !== null) {
      if (seenHotkeys.has(raw.hotkey)) {
        throw new Error(`tileStyles[${index}].hotkey must be unique within the project.`);
      }
      seenHotkeys.add(raw.hotkey);
    }
    styles.push({
      category: raw.category === undefined ? null : raw.category,
      fgColor,
      glyphIndex,
      hotkey: raw.hotkey === undefined ? null : raw.hotkey,
      id: raw.id,
      name: raw.name,
      reverse
    });
  }
  return styles;
}

function styleProjectEntry(style: TileStyle): TileMapProjectTileStyleV2 {
  return {
    category: style.category,
    fgColor: style.fgColor,
    glyphIndex: style.glyphIndex,
    hotkey: style.hotkey,
    id: style.id,
    name: style.name,
    reverse: style.reverse
  };
}

function nextImportedStyleCounter(styles: TileStyle[]): number {
  let nextCounter = styles.length + 1;
  for (const style of styles) {
    const match = /^style-(\d+)$/.exec(style.id);
    if (match === null) {
      continue;
    }
    const suffix = Number.parseInt(match[1], 10);
    if (Number.isInteger(suffix) && suffix + 1 > nextCounter) {
      nextCounter = suffix + 1;
    }
  }
  return nextCounter;
}

function projectFromDraft(draft: TileMapDraft): TileMapProjectFile {
  const base = {
    bgColor: draft.bgColor,
    borderColor: draft.borderColor,
    charsetBytes: Array.from(draft.charset.bytes),
    charsetGlyphCount: draft.charset.glyphCount,
    color: Array.from(draft.color),
    format: "am8-tilemap-project" as const,
    glyphMode: draft.glyphMode,
    height: 25 as const,
    palette: Array.from(draft.palette),
    screen: Array.from(draft.screen),
    textBit7Internal: draft.textBit7Internal,
    width: 40 as const
  };
  if (draft.tileStyles.length === 0) {
    return {
      ...base,
      version: 1
    };
  }
  return {
    ...base,
    tileStyles: draft.tileStyles.map(styleProjectEntry),
    version: 2
  };
}

function parseProject(jsonText: string, zoom: TileMapZoom): TileMapDraft {
  const raw = JSON.parse(jsonText) as Partial<TileMapProjectFile>;
  if (raw.format !== "am8-tilemap-project" || (raw.version !== 1 && raw.version !== 2)) {
    throw new Error("Tile-map project must use format 'am8-tilemap-project' version 1 or 2.");
  }
  if (raw.width !== 40 || raw.height !== 25) {
    throw new Error("Tile-map project dimensions must stay fixed at 40x25 cells.");
  }
  if (raw.glyphMode !== "128-reverse" && raw.glyphMode !== "256-direct") {
    throw new Error("Tile-map project glyphMode must be '128-reverse' or '256-direct'.");
  }
  if (raw.charsetGlyphCount !== 128 && raw.charsetGlyphCount !== 256) {
    throw new Error("Tile-map project charsetGlyphCount must be 128 or 256.");
  }
  if (raw.glyphMode === "128-reverse" && raw.charsetGlyphCount !== 128) {
    throw new Error("Tile-map projects in 128-reverse mode must carry a 128-glyph tileset.");
  }
  if (raw.glyphMode === "256-direct" && raw.charsetGlyphCount !== 256) {
    throw new Error("Tile-map projects in 256-direct mode must carry a 256-glyph tileset.");
  }
  if (typeof raw.bgColor !== "number" || !Number.isInteger(raw.bgColor) || raw.bgColor < 0 || raw.bgColor > 0x0f) {
    throw new Error("Tile-map project bgColor must be in range 0..15.");
  }
  if (
    typeof raw.borderColor !== "number" ||
    !Number.isInteger(raw.borderColor) ||
    raw.borderColor < 0 ||
    raw.borderColor > 0x0f
  ) {
    throw new Error("Tile-map project borderColor must be in range 0..15.");
  }
  if (typeof raw.textBit7Internal !== "boolean") {
    throw new Error("Tile-map project textBit7Internal must be a boolean.");
  }
  const expectedTextBit7Internal = raw.glyphMode === "128-reverse";
  if (raw.textBit7Internal !== expectedTextBit7Internal) {
    throw new Error("Tile-map project textBit7Internal must match the declared glyph mode.");
  }

  const screen = validateByteArray("screen", raw.screen, SCREEN_SIZE);
  const color = validateByteArray("color", raw.color, COLOR_SIZE);
  const palette = validatePaletteArray(raw.palette);
  const charsetBytes = validateByteArray("charsetBytes", raw.charsetBytes, raw.charsetGlyphCount * GLYPH_STRIDE);
  const tileStyles = validateStyleArray(raw.version === 2 ? raw.tileStyles : undefined, raw.charsetGlyphCount);

  const draft: TileMapDraft = {
    activeBlock: 0,
    bgColor: raw.bgColor,
    borderColor: raw.borderColor,
    charset: {
      backing: "draft",
      bytes: charsetBytes,
      glyphCount: raw.charsetGlyphCount,
      sourceLabel: "Imported project tileset"
    },
    color,
    dirty: false,
    glyphMode: raw.glyphMode,
    palette,
    screen,
    selectedCellX: 0,
    selectedCellY: 0,
    selectedColor: 1,
    selectedReverse: false,
    selectedTile: 0,
    sourceLabel: "Imported project",
    styleCounter: nextImportedStyleCounter(tileStyles),
    textBit7Internal: raw.textBit7Internal,
    tileStyles,
    zoom
  };
  syncSelectionFromCell(draft);
  return draft;
}

function downloadText(fileName: string, text: string, mimeType: string): void {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function setPaletteIndexOptions(select: HTMLSelectElement): void {
  select.replaceChildren(
    ...Array.from({ length: PALETTE_SIZE }, (_, value) => {
      const option = document.createElement("option");
      option.value = value.toString();
      option.textContent = `Palette ${value} (${formatAsmByte(value)})`;
      return option;
    })
  );
}

function setBlockOptions(select: HTMLSelectElement, glyphCount: 128 | 256, activeBlock: number): void {
  const count = glyphCount / GLYPHS_PER_BLOCK;
  select.replaceChildren(
    ...Array.from({ length: count }, (_, index) => {
      const option = document.createElement("option");
      const start = index * GLYPHS_PER_BLOCK;
      const end = start + GLYPHS_PER_BLOCK - 1;
      option.value = index.toString();
      option.textContent = `${formatHexWordPrefixed(start)}-${formatHexWordPrefixed(end)}`;
      return option;
    })
  );
  select.value = Math.max(0, Math.min(activeBlock, count - 1)).toString();
}

function isEditableElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement || target.isContentEditable;
}

function bankBaseForVideoCtrl(videoCtrl: number): number {
  return (videoCtrl & 0x03) << 14;
}

function screenBaseForMemPtr(bankBase: number, memPtr: number): number {
  return bankBase + (((memPtr >> 4) & 0x0f) << 10);
}

function charBaseForMemPtr(bankBase: number, memPtr: number): number {
  return bankBase + 0x1000 + (((memPtr >> 2) & 0x03) << 10);
}

function readActiveTextTarget(emulator: Emulator): ActiveTextTarget {
  const voc = emulator.getVocState();
  const ioc = emulator.getIocState();
  const inBitmapMode = (voc.ctrl1 & VOC_CTRL1_BMM_MASK) !== 0 || (voc.ctrl2 & VOC_CTRL2_MCM_MASK) !== 0;
  if (inBitmapMode) {
    throw new Error("Tile Map editor supports live reads only in text mode; the current live mode is not text mode.");
  }

  const bank = ioc.videoCtrl & 0x03;
  const bankBase = bankBaseForVideoCtrl(ioc.videoCtrl);
  const screenBase = screenBaseForMemPtr(bankBase, voc.memPtr);
  const colorBase = screenBase + 0x0400;
  const charOffset = (voc.memPtr >> 2) & 0x03;
  const glyphMode: TileMapGlyphMode = (voc.ctrl2 & TEXT_BIT7_INTERNAL_MASK) !== 0 ? "128-reverse" : "256-direct";
  const glyphCount: 128 | 256 = glyphMode === "128-reverse" ? 128 : 256;
  if (glyphCount === 256 && charOffset === 3) {
    throw new Error(
      "Active text tileset uses reserved CHAR_OFF=3 in 256-glyph mode; switch CHAR_OFF or enable TEXT_BIT7_INTERNAL before reading the live tile map."
    );
  }
  const tilesetBacking: CharsetBacking = bank === 0 || bank === 2 ? "rom" : "ram";
  const charBase = charBaseForMemPtr(bankBase, voc.memPtr);
  return {
    bank,
    bankBase,
    charBase,
    charOffset,
    colorBase,
    glyphCount,
    glyphMode,
    screenBase,
    textBit7Internal: glyphMode === "128-reverse",
    tilesetBacking,
    tilesetLabel:
      tilesetBacking === "rom"
        ? `Active ${glyphCount}-glyph Character ROM tileset (bank ${bank}, CHAR_OFF=${charOffset})`
        : `Active ${glyphCount}-glyph RAM tileset at ${formatHexWordPrefixed(charBase)} (bank ${bank})`
  };
}

function cellIndex(x: number, y: number): number {
  return y * CELL_COLUMNS + x;
}

function colorByteIndexForCellIndex(index: number): number {
  return index >> 1;
}

function colorNibbleAt(draft: TileMapDraft, x: number, y: number): number {
  const index = cellIndex(x, y);
  const byte = draft.color[colorByteIndexForCellIndex(index)] ?? 0;
  return (index & 1) === 0 ? byte & 0x0f : (byte >> 4) & 0x0f;
}

function setColorNibble(draft: TileMapDraft, x: number, y: number, value: number): void {
  const index = cellIndex(x, y);
  const byteIndex = colorByteIndexForCellIndex(index);
  const current = draft.color[byteIndex] ?? 0;
  if ((index & 1) === 0) {
    draft.color[byteIndex] = (current & 0xf0) | (value & 0x0f);
    return;
  }
  draft.color[byteIndex] = ((value & 0x0f) << 4) | (current & 0x0f);
}

function buildScreenCode(glyphMode: TileMapGlyphMode, tileIndex: number, reverse: boolean): number {
  const tile = tileIndex & (glyphMode === "128-reverse" ? 0x7f : 0xff);
  if (glyphMode === "128-reverse" && reverse) {
    return tile | 0x80;
  }
  return tile;
}

function selectedScreenCode(draft: TileMapDraft): number {
  return buildScreenCode(draft.glyphMode, draft.selectedTile, draft.selectedReverse);
}

function tileIndexFromScreenCode(draft: TileMapDraft, screenCode: number): number {
  return draft.glyphMode === "128-reverse" ? screenCode & 0x7f : screenCode & 0xff;
}

function reverseFromScreenCode(draft: TileMapDraft, screenCode: number): boolean {
  return draft.glyphMode === "128-reverse" && (screenCode & 0x80) !== 0;
}

function styleSignature(glyphIndex: number, fgColor: number, reverse: boolean): string {
  return `${glyphIndex}:${fgColor}:${reverse ? 1 : 0}`;
}

function styleForId(draft: TileMapDraft, styleId: string | null): TileStyle | null {
  if (styleId === null) {
    return null;
  }
  return draft.tileStyles.find((style) => style.id === styleId) ?? null;
}

function syncSelectionFromCell(draft: TileMapDraft): void {
  const screenCode = draft.screen[cellIndex(draft.selectedCellX, draft.selectedCellY)] ?? 0;
  draft.selectedTile = Math.min(tileIndexFromScreenCode(draft, screenCode), draft.charset.glyphCount - 1);
  draft.selectedReverse = reverseFromScreenCode(draft, screenCode);
  draft.selectedColor = colorNibbleAt(draft, draft.selectedCellX, draft.selectedCellY);
  draft.activeBlock = Math.floor(draft.selectedTile / GLYPHS_PER_BLOCK);
}

function rgb444Components(value: number): [number, number, number] {
  const r = ((value >> 8) & 0x0f) * 17;
  const g = ((value >> 4) & 0x0f) * 17;
  const b = (value & 0x0f) * 17;
  return [r, g, b];
}

function rgb444Css(value: number): string {
  const [r, g, b] = rgb444Components(value & 0x0fff);
  return `rgb(${r}, ${g}, ${b})`;
}

function readGlyphRowByte(charset: TileMapCharset, glyphIndex: number, row: number): number {
  if (glyphIndex < 0 || glyphIndex >= charset.glyphCount) {
    return 0;
  }
  return charset.bytes[glyphIndex * GLYPH_STRIDE + row] ?? 0;
}

function renderGlyphPreviewCanvas(
  canvas: HTMLCanvasElement,
  charset: TileMapCharset,
  palette: Uint16Array,
  bgColor: number,
  glyphIndex: number,
  fgColor: number,
  reverse: boolean
): void {
  const context = canvas.getContext("2d");
  if (context === null) {
    return;
  }
  const pixelSize = Math.max(1, Math.floor(canvas.width / CELL_DIMENSION));
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = rgb444Css(palette[bgColor] ?? 0);
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = rgb444Css(palette[fgColor] ?? 0);
  for (let row = 0; row < CELL_DIMENSION; row += 1) {
    let rowByte = readGlyphRowByte(charset, glyphIndex, row);
    if (reverse) {
      rowByte = (~rowByte) & 0xff;
    }
    for (let col = 0; col < CELL_DIMENSION; col += 1) {
      if ((rowByte & (0x80 >> col)) === 0) {
        continue;
      }
      context.fillRect(col * pixelSize, row * pixelSize, pixelSize, pixelSize);
    }
  }
}

function renderPreviewCanvas(draft: TileMapDraft, canvas: HTMLCanvasElement): void {
  const context = canvas.getContext("2d");
  if (context === null) {
    return;
  }
  const image = context.createImageData(PREVIEW_WIDTH, PREVIEW_HEIGHT);
  const data = image.data;

  for (let cellY = 0; cellY < CELL_ROWS; cellY += 1) {
    for (let cellX = 0; cellX < CELL_COLUMNS; cellX += 1) {
      const mapIndex = cellIndex(cellX, cellY);
      const screenCode = draft.screen[mapIndex] ?? 0;
      const glyphIndex = tileIndexFromScreenCode(draft, screenCode);
      const reverse = reverseFromScreenCode(draft, screenCode);
      const fg = draft.palette[colorNibbleAt(draft, cellX, cellY)] ?? 0;
      const bg = draft.palette[draft.bgColor] ?? 0;
      const [fgR, fgG, fgB] = rgb444Components(fg);
      const [bgR, bgG, bgB] = rgb444Components(bg);

      for (let row = 0; row < CELL_DIMENSION; row += 1) {
        let glyphRow = readGlyphRowByte(draft.charset, glyphIndex, row);
        if (reverse) {
          glyphRow = (~glyphRow) & 0xff;
        }
        const y = cellY * CELL_DIMENSION + row;
        for (let col = 0; col < CELL_DIMENSION; col += 1) {
          const on = (glyphRow & (0x80 >> col)) !== 0;
          const x = cellX * CELL_DIMENSION + col;
          const offset = (y * PREVIEW_WIDTH + x) * 4;
          data[offset] = on ? fgR : bgR;
          data[offset + 1] = on ? fgG : bgG;
          data[offset + 2] = on ? fgB : bgB;
          data[offset + 3] = 0xff;
        }
      }
    }
  }

  context.putImageData(image, 0, 0);
}

function renderAtlasCanvas(draft: TileMapDraft | null, canvas: HTMLCanvasElement): void {
  const context = canvas.getContext("2d");
  if (context === null) {
    return;
  }
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.imageSmoothingEnabled = false;

  if (draft === null) {
    context.fillStyle = "rgba(229, 239, 232, 0.72)";
    context.font = "14px Iosevka, monospace";
    context.fillText("Create or load a tile-map draft to render the tileset.", 16, 28);
    return;
  }

  const blockStart = draft.activeBlock * GLYPHS_PER_BLOCK;
  const pixelSize = ATLAS_GLYPH_SIZE / CELL_DIMENSION;
  const styleCountByGlyph = new Map<number, number>();
  for (const style of draft.tileStyles) {
    styleCountByGlyph.set(style.glyphIndex, (styleCountByGlyph.get(style.glyphIndex) ?? 0) + 1);
  }

  for (let row = 0; row < ATLAS_ROWS; row += 1) {
    for (let col = 0; col < ATLAS_COLUMNS; col += 1) {
      const glyphIndex = blockStart + row * ATLAS_COLUMNS + col;
      const cellX = col * ATLAS_CELL_SIZE;
      const cellY = row * ATLAS_CELL_SIZE;
      context.fillStyle = (row + col) % 2 === 0 ? "rgba(255, 255, 255, 0.04)" : "rgba(255, 255, 255, 0.07)";
      context.fillRect(cellX, cellY, ATLAS_CELL_SIZE, ATLAS_CELL_SIZE);

      if (glyphIndex < draft.charset.glyphCount) {
        const glyphX = cellX + ATLAS_GLYPH_OFFSET;
        const glyphY = cellY + ATLAS_GLYPH_OFFSET;
        const selected = glyphIndex === draft.selectedTile;
        for (let glyphRow = 0; glyphRow < CELL_DIMENSION; glyphRow += 1) {
          const rowByte = readGlyphRowByte(draft.charset, glyphIndex, glyphRow);
          for (let glyphCol = 0; glyphCol < CELL_DIMENSION; glyphCol += 1) {
            context.fillStyle = (rowByte & (0x80 >> glyphCol)) !== 0
              ? selected
                ? "#F2B84B"
                : "#E5EFE8"
              : "rgba(10, 18, 24, 0.82)";
            context.fillRect(glyphX + glyphCol * pixelSize, glyphY + glyphRow * pixelSize, pixelSize, pixelSize);
          }
        }
      }

      context.strokeStyle = glyphIndex === draft.selectedTile ? "rgba(242, 184, 75, 0.9)" : "rgba(89, 191, 186, 0.22)";
      context.lineWidth = glyphIndex === draft.selectedTile ? 2 : 1;
      context.strokeRect(cellX + 0.5, cellY + 0.5, ATLAS_CELL_SIZE - 1, ATLAS_CELL_SIZE - 1);

      const styleCount = styleCountByGlyph.get(glyphIndex) ?? 0;
      if (styleCount > 0) {
        const badgeX = cellX + ATLAS_CELL_SIZE - 7;
        const badgeY = cellY + 7;
        context.fillStyle = "rgba(242, 184, 75, 0.96)";
        context.beginPath();
        context.arc(badgeX, badgeY, 5, 0, Math.PI * 2);
        context.fill();
        if (styleCount > 1) {
          context.fillStyle = "#09141C";
          context.font = "8px Iosevka, monospace";
          context.textAlign = "center";
          context.textBaseline = "middle";
          context.fillText(String(Math.min(styleCount, 9)), badgeX, badgeY + 0.5);
        }
      }
    }
  }
}

function renderEditorCanvas(draft: TileMapDraft | null, previewCanvas: HTMLCanvasElement, canvas: HTMLCanvasElement): void {
  const context = canvas.getContext("2d");
  if (context === null) {
    return;
  }

  if (draft === null) {
    canvas.width = PREVIEW_WIDTH * 2;
    canvas.height = PREVIEW_HEIGHT * 2;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "rgba(229, 239, 232, 0.72)";
    context.font = "14px Iosevka, monospace";
    context.fillText("Create or load a tile-map draft to edit cells.", 16, 28);
    return;
  }

  const width = PREVIEW_WIDTH * draft.zoom;
  const height = PREVIEW_HEIGHT * draft.zoom;
  if (canvas.width !== width) {
    canvas.width = width;
  }
  if (canvas.height !== height) {
    canvas.height = height;
  }

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.imageSmoothingEnabled = false;
  context.drawImage(previewCanvas, 0, 0, previewCanvas.width, previewCanvas.height, 0, 0, width, height);

  context.strokeStyle = "rgba(89, 191, 186, 0.12)";
  context.lineWidth = 1;
  for (let col = 0; col <= CELL_COLUMNS; col += 1) {
    const x = col * CELL_DIMENSION * draft.zoom + 0.5;
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x, height);
    context.stroke();
  }
  for (let row = 0; row <= CELL_ROWS; row += 1) {
    const y = row * CELL_DIMENSION * draft.zoom + 0.5;
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(width, y);
    context.stroke();
  }

  const selectedX = draft.selectedCellX * CELL_DIMENSION * draft.zoom;
  const selectedY = draft.selectedCellY * CELL_DIMENSION * draft.zoom;
  context.strokeStyle = "rgba(242, 184, 75, 0.96)";
  context.lineWidth = 2;
  context.strokeRect(selectedX + 1, selectedY + 1, CELL_DIMENSION * draft.zoom - 2, CELL_DIMENSION * draft.zoom - 2);
}

function renderPaletteSelectBackground(select: HTMLSelectElement, palette: Uint16Array, value: number): void {
  select.style.background = `linear-gradient(180deg, rgba(8, 18, 25, 0.92), rgba(8, 18, 25, 0.92)), ${rgb444Css(palette[value] ?? 0)}`;
}

function mapPointToCell(event: MouseEvent | PointerEvent, canvas: HTMLCanvasElement): { x: number; y: number } | null {
  const bounds = canvas.getBoundingClientRect();
  const x = Math.floor(((event.clientX - bounds.left) / bounds.width) * CELL_COLUMNS);
  const y = Math.floor(((event.clientY - bounds.top) / bounds.height) * CELL_ROWS);
  if (x < 0 || x >= CELL_COLUMNS || y < 0 || y >= CELL_ROWS) {
    return null;
  }
  return { x, y };
}

function atlasPointToGlyph(event: MouseEvent, canvas: HTMLCanvasElement, activeBlock: number, glyphCount: 128 | 256): number | null {
  const bounds = canvas.getBoundingClientRect();
  const col = Math.floor(((event.clientX - bounds.left) / bounds.width) * ATLAS_COLUMNS);
  const row = Math.floor(((event.clientY - bounds.top) / bounds.height) * ATLAS_ROWS);
  if (col < 0 || col >= ATLAS_COLUMNS || row < 0 || row >= ATLAS_ROWS) {
    return null;
  }
  const glyph = activeBlock * GLYPHS_PER_BLOCK + row * ATLAS_COLUMNS + col;
  return glyph < glyphCount ? glyph : null;
}

function buildByteBlock(label: string, bytes: Uint8Array, bytesPerLine: number): string {
  const lines = [`${label}:`];
  for (let offset = 0; offset < bytes.length; offset += bytesPerLine) {
    const slice = bytes.slice(offset, offset + bytesPerLine);
    lines.push(`  .byte ${Array.from(slice, (value) => formatAsmByte(value)).join(", ")}`);
  }
  return lines.join("\n");
}

function buildPaletteBytes(palette: Uint16Array): Uint8Array {
  const bytes = new Uint8Array(PALETTE_SIZE * 2);
  for (let index = 0; index < PALETTE_SIZE; index += 1) {
    const value = palette[index] ?? 0;
    bytes[index * 2] = value & 0xff;
    bytes[index * 2 + 1] = (value >> 8) & 0x0f;
  }
  return bytes;
}

function buildByteOutput(draft: TileMapDraft | null): string {
  if (draft === null) {
    return "";
  }

  return [
    `; Tile Map export`,
    `; glyph mode: ${draft.glyphMode}`,
    `; textBit7Internal: ${draft.textBit7Internal ? "1" : "0"}`,
    buildByteBlock("tilemap_bg_color", new Uint8Array([draft.bgColor & 0x0f]), 1),
    buildByteBlock("tilemap_border_color", new Uint8Array([draft.borderColor & 0x0f]), 1),
    buildByteBlock("tilemap_palette", buildPaletteBytes(draft.palette), 16),
    buildByteBlock("tilemap_screen", draft.screen, CELL_COLUMNS),
    buildByteBlock("tilemap_color", draft.color, 20),
    buildByteBlock("tilemap_charset", draft.charset.bytes, 16)
  ].join("\n\n");
}

function createBlankDraft(
  glyphMode: TileMapGlyphMode,
  palette: Uint16Array,
  bgColor: number,
  borderColor: number,
  zoom: TileMapZoom,
  sourceLabel: string
): TileMapDraft {
  const glyphCount: 128 | 256 = glyphMode === "128-reverse" ? 128 : 256;
  return {
    activeBlock: 0,
    bgColor,
    borderColor,
    charset: {
      backing: "draft",
      bytes: new Uint8Array(glyphCount * GLYPH_STRIDE),
      glyphCount,
      sourceLabel: `Blank ${glyphCount}-glyph tileset`
    },
    color: new Uint8Array(COLOR_SIZE),
    dirty: false,
    glyphMode,
    palette: clonePalette(palette),
    screen: new Uint8Array(SCREEN_SIZE),
    selectedCellX: 0,
    selectedCellY: 0,
    selectedColor: 1,
    selectedReverse: false,
    selectedTile: 0,
    sourceLabel,
    styleCounter: 1,
    textBit7Internal: glyphMode === "128-reverse",
    tileStyles: [],
    zoom
  };
}

function importCharacterTilesetForDraft(
  currentDraft: TileMapDraft,
  transfer: CharacterDraftTransfer
): { bytes: Uint8Array; glyphCount: 128 | 256; sourceLabel: string } {
  if (currentDraft.glyphMode === "128-reverse") {
    if (transfer.glyphCount === 128) {
      return {
        bytes: cloneBytes(transfer.bytes),
        glyphCount: 128,
        sourceLabel: `Character Editor draft (${transfer.sourceLabel})`
      };
    }
    if (transfer.glyphCount === 512) {
      const startBlock = Math.max(0, Math.min(transfer.activeBlock, 3));
      const startOffset = startBlock * GLYPHS_PER_BLOCK * GLYPH_STRIDE;
      return {
        bytes: transfer.bytes.slice(startOffset, startOffset + GLYPHS_PER_BLOCK * GLYPH_STRIDE),
        glyphCount: 128,
        sourceLabel: `Character Editor block ${startBlock} from ${transfer.sourceLabel}`
      };
    }
    throw new Error(
      "Current tile-map draft uses 128+reverse mode. Load a 128-glyph Character draft or select a 128-glyph block from a 512-glyph draft first."
    );
  }

  if (transfer.glyphCount === 256) {
    return {
      bytes: cloneBytes(transfer.bytes),
      glyphCount: 256,
      sourceLabel: `Character Editor draft (${transfer.sourceLabel})`
    };
  }
  if (transfer.glyphCount === 512) {
    const startBlock = transfer.activeBlock;
    if (startBlock < 0 || startBlock > 2) {
      throw new Error("Current Character draft block must be 0, 1, or 2 to hand off 256 glyphs into the tile-map editor.");
    }
    const startOffset = startBlock * GLYPHS_PER_BLOCK * GLYPH_STRIDE;
    return {
      bytes: transfer.bytes.slice(startOffset, startOffset + GLYPHS_PER_BLOCK * 2 * GLYPH_STRIDE),
      glyphCount: 256,
      sourceLabel: `Character Editor blocks ${startBlock}-${startBlock + 1} from ${transfer.sourceLabel}`
    };
  }
  throw new Error(
    "Current tile-map draft uses 256-direct mode. Load a 256-glyph Character draft or select a 256-glyph range from a 512-glyph draft first."
  );
}

export function setupTileMapEditor(
  emulator: Emulator,
  elements: TileMapEditorElements,
  interop: TileMapEditorInterop
): TileMapEditorController {
  const menuHost = document.getElementById("tile-map-menu-bar");
  if (!(menuHost instanceof HTMLDivElement)) {
    throw new Error("missing tile-map menu bar host");
  }
  let draft: TileMapDraft | null = null;
  let brushMode: TileStyleBrushMode = "tile-current-color";
  let currentTool: TileMapTool = "stamp";
  let leftPanelCollapsed = false;
  let pendingRectangleAnchor: PaintPoint | null = null;
  let paintStrokeSnapshot: TileMapDraft | null = null;
  let paintStrokeDirty = false;
  let recentStyleIds: string[] = [];
  let rightPanelCollapsed = false;
  let selectedStyleId: string | null = null;
  let suggestedStyles: SuggestedTileStyle[] = [];
  let menuBar: WorkbenchMenuBarController | null = null;
  const history: HistoryManager<TileMapDraft> = createHistoryManager(cloneDraft, 64);
  const tools: ToolButtonView[] = (["stamp", "fill", "rectangle"] as TileMapTool[]).map((value) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "image-tool-button tile-map-tool-button";
    button.textContent = toolLabel(value);
    button.addEventListener("click", () => {
      currentTool = value;
      pendingRectangleAnchor = null;
      setStatus(`Selected the ${toolLabel(value).toLowerCase()} tool.`);
      renderAll();
    });
    return { button, value };
  });
  elements.toolButtons.replaceChildren(...tools.map((tool) => tool.button));
  setPaletteIndexOptions(elements.fgColorSelect);
  setPaletteIndexOptions(elements.bgColorSelect);
  setPaletteIndexOptions(elements.borderColorSelect);
  elements.zoomSelect.value = "4";

  const setStatus = (message: string): void => {
    elements.status.textContent = message;
  };

  const refreshMenuBar = (): void => {
    menuBar?.refresh();
  };

  const resetStyleUiState = (): void => {
    brushMode = "tile-current-color";
    recentStyleIds = [];
    selectedStyleId = null;
    suggestedStyles = [];
  };

  const removeRecentStyle = (styleId: string): void => {
    recentStyleIds = recentStyleIds.filter((entry) => entry !== styleId);
  };

  const touchRecentStyle = (styleId: string): void => {
    recentStyleIds = [styleId, ...recentStyleIds.filter((entry) => entry !== styleId)].slice(0, 8);
  };

  const styleDisplayOrder = (currentDraft: TileMapDraft): TileStyle[] => {
    const recencyRank = new Map<string, number>();
    recentStyleIds.forEach((styleId, index) => {
      recencyRank.set(styleId, index);
    });
    const selectedGlyph = currentDraft.selectedTile;
    return [...currentDraft.tileStyles].sort((left, right) => {
      const leftRecent = recencyRank.get(left.id);
      const rightRecent = recencyRank.get(right.id);
      if (leftRecent !== undefined || rightRecent !== undefined) {
        if (leftRecent === undefined) return 1;
        if (rightRecent === undefined) return -1;
        if (leftRecent !== rightRecent) return leftRecent - rightRecent;
      }
      const leftGlyphMatch = left.glyphIndex === selectedGlyph ? 0 : 1;
      const rightGlyphMatch = right.glyphIndex === selectedGlyph ? 0 : 1;
      if (leftGlyphMatch !== rightGlyphMatch) {
        return leftGlyphMatch - rightGlyphMatch;
      }
      return left.name.localeCompare(right.name);
    });
  };

  const quickStyleOrder = (currentDraft: TileMapDraft): TileStyle[] => {
    const byId = new Map(currentDraft.tileStyles.map((style) => [style.id, style] as const));
    const ordered: TileStyle[] = [];
    for (const styleId of recentStyleIds) {
      const style = byId.get(styleId);
      if (style !== undefined) {
        ordered.push(style);
      }
    }
    for (const style of styleDisplayOrder(currentDraft)) {
      if (ordered.some((entry) => entry.id === style.id)) {
        continue;
      }
      ordered.push(style);
      if (ordered.length >= 8) {
        break;
      }
    }
    return ordered.slice(0, 8);
  };

  const updatePanelRails = (): void => {
    elements.panel.dataset.leftCollapsed = leftPanelCollapsed ? "true" : "false";
    elements.panel.dataset.rightCollapsed = rightPanelCollapsed ? "true" : "false";
    elements.leftRail.setAttribute("aria-expanded", leftPanelCollapsed ? "false" : "true");
    elements.rightRail.setAttribute("aria-expanded", rightPanelCollapsed ? "false" : "true");
    elements.leftRail.textContent = leftPanelCollapsed ? "SHOW" : "HIDE";
    elements.rightRail.textContent = rightPanelCollapsed ? "SHOW" : "HIDE";
    elements.leftRail.title = leftPanelCollapsed ? "Show tileset panel" : "Hide tileset panel";
    elements.rightRail.title = rightPanelCollapsed ? "Show inspector panel" : "Hide inspector panel";
    elements.leftRail.setAttribute("aria-label", leftPanelCollapsed ? "Show tileset panel" : "Hide tileset panel");
    elements.rightRail.setAttribute("aria-label", rightPanelCollapsed ? "Show inspector panel" : "Hide inspector panel");
    elements.layout.classList.toggle("is-left-collapsed", leftPanelCollapsed);
    elements.layout.classList.toggle("is-right-collapsed", rightPanelCollapsed);
  };

  const updateRailOffset = (): void => {
    const panelRect = elements.panel.getBoundingClientRect();
    const tilesetCard = elements.layout.querySelector<HTMLElement>(".tile-map-card--tileset");
    const inspectorCard = elements.layout.querySelector<HTMLElement>(".tile-map-card--inspector");
    const mapCard = elements.layout.querySelector<HTMLElement>(".tile-map-card--map");
    const referenceCard = tilesetCard ?? inspectorCard ?? mapCard;
    if (referenceCard === null) {
      return;
    }
    const cardRect = referenceCard.getBoundingClientRect();
    const nextOffset = Math.max(0, Math.round(cardRect.top - panelRect.top + 18));
    elements.panel.style.setProperty("--tile-map-rail-offset", `${nextOffset}px`);
  };

  const draftOrThrow = (): TileMapDraft => {
    if (draft === null) {
      throw new Error("Create or load a tile-map draft first.");
    }
    return draft;
  };

  const selectedStyle = (): TileStyle | null => {
    if (draft === null) {
      return null;
    }
    return styleForId(draft, selectedStyleId);
  };

  const ensureSelectedStyleIsValid = (): void => {
    if (draft === null) {
      selectedStyleId = null;
      return;
    }
    if (selectedStyleId !== null && styleForId(draft, selectedStyleId) === null) {
      selectedStyleId = null;
      if (brushMode === "tile-style") {
        brushMode = "tile-current-color";
      }
    }
  };

  const resolveBrush = (currentDraft: TileMapDraft): ResolvedBrush => {
    if (brushMode === "tile-only") {
      return {
        fgColor: currentDraft.selectedColor & 0x0f,
        screenCode: selectedScreenCode(currentDraft),
        writesColor: false
      };
    }
    if (brushMode === "tile-current-color") {
      return {
        fgColor: currentDraft.selectedColor & 0x0f,
        screenCode: selectedScreenCode(currentDraft),
        writesColor: true
      };
    }
    const style = selectedStyle();
    if (style === null) {
      throw new Error("Select a tile style before painting in Tile Style mode.");
    }
    if (style.glyphIndex >= currentDraft.charset.glyphCount) {
      throw new Error("The selected tile style no longer fits the current tileset. Choose another style or tileset.");
    }
    return {
      fgColor: style.fgColor & 0x0f,
      screenCode: buildScreenCode(currentDraft.glyphMode, style.glyphIndex, style.reverse),
      writesColor: true
    };
  };

  const historyStateText = (): string => {
    const state = history.getState();
    const undoText = state.lastUndoLabel === null ? `${state.undoDepth}` : `${state.undoDepth} (${state.lastUndoLabel})`;
    const redoText = state.lastRedoLabel === null ? `${state.redoDepth}` : `${state.redoDepth} (${state.lastRedoLabel})`;
    return `Undo ${undoText} | Redo ${redoText}`;
  };

  const confirmDiscardDirtyDraft = (action: string): boolean => {
    if (draft === null || !draft.dirty) {
      return true;
    }
    return window.confirm(`Discard unsaved tile-map edits before ${action}?`);
  };

  const defaultGlyphMode = (): TileMapGlyphMode => {
    try {
      return readActiveTextTarget(emulator).glyphMode;
    } catch {
      return "256-direct";
    }
  };

  const defaultPalette = (): Uint16Array => clonePalette(emulator.getVocState().palette);

  const defaultBgColor = (): number => emulator.getVocState().bgColor & 0x0f;

  const defaultBorderColor = (): number => emulator.getVocState().borderColor & 0x0f;

  const commitPaintStroke = (): void => {
    if (paintStrokeSnapshot === null || !paintStrokeDirty || draft === null) {
      paintStrokeSnapshot = null;
      paintStrokeDirty = false;
      return;
    }
    history.pushUndoSnapshot("Paint tile cells", paintStrokeSnapshot);
    paintStrokeSnapshot = null;
    paintStrokeDirty = false;
  };

  const replaceDraft = (nextDraft: TileMapDraft, message: string): void => {
    commitPaintStroke();
    draft = nextDraft;
    history.clear();
    pendingRectangleAnchor = null;
    resetStyleUiState();
    syncSelectionFromCell(nextDraft);
    setStatus(message);
    renderAll();
  };

  const mutateDraft = (statusMessage: string, historyLabel: string, mutation: (currentDraft: TileMapDraft) => void): void => {
    const currentDraft = draftOrThrow();
    commitPaintStroke();
    history.pushUndoSnapshot(historyLabel, currentDraft);
    mutation(currentDraft);
    currentDraft.dirty = true;
    setStatus(statusMessage);
    renderAll();
  };

  const selectCell = (x: number, y: number, message: string | null = null): void => {
    const currentDraft = draftOrThrow();
    currentDraft.selectedCellX = x;
    currentDraft.selectedCellY = y;
    syncSelectionFromCell(currentDraft);
    if (message !== null) {
      setStatus(message);
    }
    renderAll();
  };

  const applyResolvedBrushToCell = (currentDraft: TileMapDraft, x: number, y: number, brush: ResolvedBrush): boolean => {
    const index = cellIndex(x, y);
    const nextScreenCode = brush.screenCode;
    const nextColor = brush.fgColor & 0x0f;
    const currentScreenCode = currentDraft.screen[index] ?? 0;
    const currentColor = colorNibbleAt(currentDraft, x, y);
    if (currentScreenCode === nextScreenCode && (!brush.writesColor || currentColor === nextColor)) {
      currentDraft.selectedCellX = x;
      currentDraft.selectedCellY = y;
      syncSelectionFromCell(currentDraft);
      return false;
    }
    currentDraft.screen[index] = nextScreenCode;
    if (brush.writesColor) {
      setColorNibble(currentDraft, x, y, nextColor);
    }
    currentDraft.selectedCellX = x;
    currentDraft.selectedCellY = y;
    syncSelectionFromCell(currentDraft);
    return true;
  };

  const applyStampAt = (x: number, y: number): void => {
    const currentDraft = draftOrThrow();
    const brush = resolveBrush(currentDraft);
    if (paintStrokeSnapshot === null) {
      paintStrokeSnapshot = cloneDraft(currentDraft);
    }
    if (!applyResolvedBrushToCell(currentDraft, x, y, brush)) {
      renderAll();
      return;
    }
    paintStrokeDirty = true;
    currentDraft.dirty = true;
    setStatus(`Painted cell ${x}, ${y} with screen code ${formatHexBytePrefixed(brush.screenCode)}.`);
    renderAll();
  };

  const applyFillAt = (x: number, y: number): void => {
    const currentDraft = draftOrThrow();
    const brush = resolveBrush(currentDraft);
    const replacementCode = brush.writesColor ? ((brush.screenCode & 0xff) << 4) | (brush.fgColor & 0x0f) : brush.screenCode & 0xff;
    const points = rasterizeFloodFill(
      { x, y },
      CELL_COLUMNS,
      CELL_ROWS,
      (cellX, cellY) => {
        const currentCode = currentDraft.screen[cellIndex(cellX, cellY)] ?? 0;
        const currentFg = colorNibbleAt(currentDraft, cellX, cellY);
        return brush.writesColor ? (currentCode << 4) | currentFg : currentCode;
      },
      replacementCode
    );
    if (points.length === 0) {
      selectCell(x, y, `Fill target at ${x}, ${y} already matches the current tile and color.`);
      return;
    }
    mutateDraft(`Filled ${points.length} tile-map cell${points.length === 1 ? "" : "s"} from ${x}, ${y}.`, "Fill tile cells", (draftToMutate) => {
      for (const point of points) {
        applyResolvedBrushToCell(draftToMutate, point.x, point.y, brush);
      }
    });
  };

  const applyRectangleAt = (x: number, y: number): void => {
    if (pendingRectangleAnchor === null) {
      pendingRectangleAnchor = { x, y };
      selectCell(x, y, `Rectangle start point set at ${x}, ${y}. Click another cell to finish the outline.`);
      return;
    }
    const anchor = pendingRectangleAnchor;
    const shape = rasterizeRectangleOutline(anchor, { x, y });
    const brush = resolveBrush(draftOrThrow());
    pendingRectangleAnchor = null;
    mutateDraft(
      `Drew a tile rectangle from ${anchor.x}, ${anchor.y} to ${x}, ${y}.`,
      "Draw tile rectangle",
      (draftToMutate) => {
        for (const point of shape) {
          applyResolvedBrushToCell(draftToMutate, point.x, point.y, brush);
        }
      }
    );
  };

  const readActive = (): void => {
    if (!confirmDiscardDirtyDraft("reading the active text screen")) {
      setStatus("Kept the current tile-map draft.");
      return;
    }

    const target = readActiveTextTarget(emulator);
    const screen = emulator.copyMemory(target.screenBase, SCREEN_SIZE);
    const color = emulator.copyMemory(target.colorBase, COLOR_SIZE);
    const palette = clonePalette(emulator.getVocState().palette);
    const charsetBytes =
      target.tilesetBacking === "rom"
        ? emulator.copyCharacterRom(target.charOffset << 10, target.glyphCount * GLYPH_STRIDE)
        : emulator.copyMemory(target.charBase, target.glyphCount * GLYPH_STRIDE);
    replaceDraft(
      {
        activeBlock: 0,
        bgColor: emulator.getVocState().bgColor & 0x0f,
        borderColor: emulator.getVocState().borderColor & 0x0f,
        charset: {
          backing: target.tilesetBacking,
          bytes: charsetBytes,
          glyphCount: target.glyphCount,
          sourceLabel: target.tilesetLabel
        },
        color,
        dirty: false,
        glyphMode: target.glyphMode,
        palette,
        screen,
        selectedCellX: 0,
        selectedCellY: 0,
        selectedColor: 1,
        selectedReverse: false,
        selectedTile: 0,
        sourceLabel: `Active text screen at ${formatHexWordPrefixed(target.screenBase)}`,
        styleCounter: 1,
        textBit7Internal: target.textBit7Internal,
        tileStyles: [],
        zoom: draft?.zoom ?? 4
      },
      `Loaded the active ${glyphModeLabel(target.glyphMode).toLowerCase()} text screen into the local tile-map draft.`
    );
  };

  const newBlank = (): void => {
    if (!confirmDiscardDirtyDraft("creating a blank tile map")) {
      setStatus("Kept the current tile-map draft.");
      return;
    }
    const glyphMode = draft?.glyphMode ?? defaultGlyphMode();
    replaceDraft(
      createBlankDraft(glyphMode, defaultPalette(), defaultBgColor(), defaultBorderColor(), draft?.zoom ?? 4, "Blank tile map"),
      `Created a blank ${glyphModeLabel(glyphMode).toLowerCase()} tile-map draft.`
    );
  };

  const exportProject = (): void => {
    const currentDraft = draftOrThrow();
    downloadText(
      `tilemap-${currentDraft.glyphMode}.am8tm.json`,
      JSON.stringify(projectFromDraft(currentDraft), null, 2),
      "application/json"
    );
    setStatus("Exported the full tile-map project, including the current tileset.");
  };

  const exportByte = (): void => {
    const currentDraft = draftOrThrow();
    downloadText(`tilemap-${currentDraft.glyphMode}.asm`, buildByteOutput(currentDraft), "text/plain");
    setStatus("Exported the tile map, colors, palette snapshot, and tileset as assembler .byte text.");
  };

  const importProject = async (file: File): Promise<void> => {
    if (!confirmDiscardDirtyDraft(`importing ${file.name || "a tile-map project"}`)) {
      setStatus("Kept the current tile-map draft.");
      return;
    }
    const imported = parseProject(await file.text(), draft?.zoom ?? 4);
    replaceDraft(imported, `Imported tile-map project from ${file.name || "the selected file"}.`);
  };

  const nextStyleName = (currentDraft: TileMapDraft, prefix = "Style"): string => `${prefix} ${currentDraft.styleCounter}`;

  const createStyleRecord = (
    currentDraft: TileMapDraft,
    name: string,
    glyphIndex: number,
    fgColor: number,
    reverse: boolean
  ): TileStyle => {
    while (currentDraft.tileStyles.some((style) => style.id === `style-${currentDraft.styleCounter}`)) {
      currentDraft.styleCounter += 1;
    }
    const style: TileStyle = {
      category: null,
      fgColor: fgColor & 0x0f,
      glyphIndex,
      hotkey: null,
      id: `style-${currentDraft.styleCounter}`,
      name,
      reverse
    };
    currentDraft.styleCounter += 1;
    return style;
  };

  const saveCurrentBrushAsStyle = (): void => {
    mutateDraft(`Saved the current brush as tile style '${nextStyleName(draftOrThrow())}'.`, "Save tile style", (draftToMutate) => {
      const style = createStyleRecord(
        draftToMutate,
        nextStyleName(draftToMutate),
        Math.min(draftToMutate.selectedTile, draftToMutate.charset.glyphCount - 1),
        draftToMutate.selectedColor,
        draftToMutate.glyphMode === "128-reverse" ? draftToMutate.selectedReverse : false
      );
      draftToMutate.tileStyles.push(style);
      selectedStyleId = style.id;
      brushMode = "tile-style";
      touchRecentStyle(style.id);
    });
  };

  const createStyleFromCurrentCell = (): void => {
    const currentDraft = draftOrThrow();
    const screenCode = currentDraft.screen[cellIndex(currentDraft.selectedCellX, currentDraft.selectedCellY)] ?? 0;
    const glyphIndex = tileIndexFromScreenCode(currentDraft, screenCode);
    const reverse = reverseFromScreenCode(currentDraft, screenCode);
    const fgColor = colorNibbleAt(currentDraft, currentDraft.selectedCellX, currentDraft.selectedCellY);
    mutateDraft(
      `Created a tile style from cell ${currentDraft.selectedCellX}, ${currentDraft.selectedCellY}.`,
      "Create tile style from cell",
      (draftToMutate) => {
        const style = createStyleRecord(
          draftToMutate,
          `Tile ${glyphIndex} / FG ${fgColor}`,
          glyphIndex,
          fgColor,
          reverse
        );
        draftToMutate.tileStyles.push(style);
        selectedStyleId = style.id;
        brushMode = "tile-style";
        touchRecentStyle(style.id);
      }
    );
  };

  const useStyle = (styleId: string): void => {
    const currentDraft = draftOrThrow();
    const style = styleForId(currentDraft, styleId);
    if (style === null) {
      throw new Error("The selected tile style no longer exists.");
    }
    selectedStyleId = style.id;
    brushMode = "tile-style";
    currentDraft.selectedTile = style.glyphIndex;
    currentDraft.selectedColor = style.fgColor;
    currentDraft.selectedReverse = style.reverse;
    currentDraft.activeBlock = Math.floor(style.glyphIndex / GLYPHS_PER_BLOCK);
    touchRecentStyle(style.id);
    setStatus(`Selected tile style '${style.name}'.`);
    renderAll();
  };

  const renameStyle = (styleId: string): void => {
    const currentDraft = draftOrThrow();
    const style = styleForId(currentDraft, styleId);
    if (style === null) {
      throw new Error("The selected tile style no longer exists.");
    }
    const nextName = window.prompt("Rename tile style", style.name);
    if (nextName === null) {
      setStatus("Kept the current tile style name.");
      return;
    }
    const trimmed = nextName.trim();
    if (trimmed === "") {
      setStatus("Tile style names cannot be empty.");
      return;
    }
    mutateDraft(`Renamed tile style '${style.name}' to '${trimmed}'.`, "Rename tile style", (draftToMutate) => {
      const target = styleForId(draftToMutate, styleId);
      if (target !== null) {
        target.name = trimmed;
      }
    });
  };

  const updateStyleFromBrush = (styleId: string): void => {
    const currentDraft = draftOrThrow();
    const style = styleForId(currentDraft, styleId);
    if (style === null) {
      throw new Error("The selected tile style no longer exists.");
    }
    mutateDraft(`Updated tile style '${style.name}' from the current brush.`, "Update tile style from brush", (draftToMutate) => {
      const target = styleForId(draftToMutate, styleId);
      if (target !== null) {
        target.glyphIndex = Math.min(draftToMutate.selectedTile, draftToMutate.charset.glyphCount - 1);
        target.fgColor = draftToMutate.selectedColor & 0x0f;
        target.reverse = draftToMutate.glyphMode === "128-reverse" ? draftToMutate.selectedReverse : false;
      }
    });
  };

  const setStyleCategory = (styleId: string): void => {
    const currentDraft = draftOrThrow();
    const style = styleForId(currentDraft, styleId);
    if (style === null) {
      throw new Error("The selected tile style no longer exists.");
    }
    const nextCategory = window.prompt("Set tile style category (leave empty to clear)", style.category ?? "");
    if (nextCategory === null) {
      setStatus("Kept the current tile style category.");
      return;
    }
    const trimmed = nextCategory.trim();
    mutateDraft(
      trimmed === "" ? `Cleared the category for tile style '${style.name}'.` : `Updated the category for tile style '${style.name}'.`,
      "Set tile style category",
      (draftToMutate) => {
        const target = styleForId(draftToMutate, styleId);
        if (target !== null) {
          target.category = trimmed === "" ? null : trimmed;
        }
      }
    );
  };

  const assignStyleHotkey = (styleId: string, hotkey: number | null): void => {
    const currentDraft = draftOrThrow();
    const style = styleForId(currentDraft, styleId);
    if (style === null) {
      throw new Error("The selected tile style no longer exists.");
    }
    mutateDraft(
      hotkey === null ? `Cleared the hotkey for tile style '${style.name}'.` : `Assigned hotkey ${hotkey} to tile style '${style.name}'.`,
      "Assign tile style hotkey",
      (draftToMutate) => {
        for (const entry of draftToMutate.tileStyles) {
          if (entry.id !== styleId && entry.hotkey === hotkey) {
            entry.hotkey = null;
          }
        }
        const target = styleForId(draftToMutate, styleId);
        if (target !== null) {
          target.hotkey = hotkey;
        }
      }
    );
  };

  const deleteStyle = (styleId: string): void => {
    const currentDraft = draftOrThrow();
    const style = styleForId(currentDraft, styleId);
    if (style === null) {
      throw new Error("The selected tile style no longer exists.");
    }
    mutateDraft(`Deleted tile style '${style.name}'.`, "Delete tile style", (draftToMutate) => {
      draftToMutate.tileStyles = draftToMutate.tileStyles.filter((entry) => entry.id !== styleId);
      removeRecentStyle(styleId);
      if (selectedStyleId === styleId) {
        selectedStyleId = null;
        if (brushMode === "tile-style") {
          brushMode = "tile-current-color";
        }
      }
    });
  };

  const analyzeSuggestedStyles = (): void => {
    const currentDraft = draftOrThrow();
    const existing = new Set(currentDraft.tileStyles.map((style) => styleSignature(style.glyphIndex, style.fgColor, style.reverse)));
    const counts = new Map<string, SuggestedTileStyle>();
    for (let y = 0; y < CELL_ROWS; y += 1) {
      for (let x = 0; x < CELL_COLUMNS; x += 1) {
        const screenCode = currentDraft.screen[cellIndex(x, y)] ?? 0;
        const glyphIndex = tileIndexFromScreenCode(currentDraft, screenCode);
        const reverse = reverseFromScreenCode(currentDraft, screenCode);
        const fgColor = colorNibbleAt(currentDraft, x, y);
        const signature = styleSignature(glyphIndex, fgColor, reverse);
        if (existing.has(signature)) {
          continue;
        }
        const entry = counts.get(signature);
        if (entry === undefined) {
          counts.set(signature, { count: 1, fgColor, glyphIndex, reverse, signature });
        } else {
          entry.count += 1;
        }
      }
    }
    suggestedStyles = [...counts.values()]
      .sort((left, right) => right.count - left.count || left.glyphIndex - right.glyphIndex || left.fgColor - right.fgColor)
      .slice(0, 8);
    setStatus(
      suggestedStyles.length === 0
        ? "No additional tile-style suggestions were found for the current map."
        : `Generated ${suggestedStyles.length} tile-style suggestion${suggestedStyles.length === 1 ? "" : "s"} from the current map.`
    );
    renderAll();
  };

  const addSuggestedStyle = (suggestion: SuggestedTileStyle): void => {
    mutateDraft(`Added a suggested tile style for glyph ${suggestion.glyphIndex}.`, "Add suggested tile style", (draftToMutate) => {
      const style = createStyleRecord(
        draftToMutate,
        `Suggested ${suggestion.glyphIndex} / FG ${suggestion.fgColor}`,
        suggestion.glyphIndex,
        suggestion.fgColor,
        suggestion.reverse
      );
      draftToMutate.tileStyles.push(style);
      selectedStyleId = style.id;
      brushMode = "tile-style";
      touchRecentStyle(style.id);
      suggestedStyles = suggestedStyles.filter((entry) => entry.signature !== suggestion.signature);
    });
  };

  const applyStyleToSelection = (): void => {
    const currentDraft = draftOrThrow();
    const style = selectedStyle();
    if (style === null) {
      throw new Error("Select a tile style before applying it to the current cell.");
    }
    const brush: ResolvedBrush = {
      fgColor: style.fgColor,
      screenCode: buildScreenCode(currentDraft.glyphMode, style.glyphIndex, style.reverse),
      writesColor: true
    };
    mutateDraft(`Applied tile style '${style.name}' to the selected cell.`, "Apply tile style to selection", (draftToMutate) => {
      applyResolvedBrushToCell(draftToMutate, draftToMutate.selectedCellX, draftToMutate.selectedCellY, brush);
    });
  };

  const applyStyleToMatchingCells = (): void => {
    const currentDraft = draftOrThrow();
    const style = selectedStyle();
    if (style === null) {
      throw new Error("Select a tile style before applying it to matching cells.");
    }
    const targetGlyph = tileIndexFromScreenCode(
      currentDraft,
      currentDraft.screen[cellIndex(currentDraft.selectedCellX, currentDraft.selectedCellY)] ?? 0
    );
    if (!window.confirm(`Apply tile style '${style.name}' to all cells that currently use glyph ${targetGlyph}?`)) {
      setStatus("Kept the current tile map unchanged.");
      return;
    }
    const brush: ResolvedBrush = {
      fgColor: style.fgColor,
      screenCode: buildScreenCode(currentDraft.glyphMode, style.glyphIndex, style.reverse),
      writesColor: true
    };
    let changedCount = 0;
    mutateDraft(`Applied tile style '${style.name}' to matching glyph cells.`, "Apply tile style to matching cells", (draftToMutate) => {
      for (let y = 0; y < CELL_ROWS; y += 1) {
        for (let x = 0; x < CELL_COLUMNS; x += 1) {
          const glyphIndex = tileIndexFromScreenCode(draftToMutate, draftToMutate.screen[cellIndex(x, y)] ?? 0);
          if (glyphIndex !== targetGlyph) {
            continue;
          }
          if (applyResolvedBrushToCell(draftToMutate, x, y, brush)) {
            changedCount += 1;
          }
        }
      }
    });
    setStatus(`Applied tile style '${style.name}' to ${changedCount} matching cell${changedCount === 1 ? "" : "s"}.`);
    renderAll();
  };

  const useCharacterDraftAsTileset = (): void => {
    const currentDraft = draftOrThrow();
    const transfer = interop.getCharacterDraft();
    if (transfer === null) {
      throw new Error("Load or create a Character Editor draft first, then use it as the tile-map tileset.");
    }

    let removedStyles = 0;
    mutateDraft("Copied the current Character Editor draft into the tile-map tileset.", "Use Character draft as tileset", (draftToMutate) => {
      const imported = importCharacterTilesetForDraft(draftToMutate, transfer);
      draftToMutate.charset = {
        backing: "draft",
        bytes: imported.bytes,
        glyphCount: imported.glyphCount,
        sourceLabel: imported.sourceLabel
      };
      draftToMutate.selectedTile = Math.min(draftToMutate.selectedTile, imported.glyphCount - 1);
      draftToMutate.activeBlock = Math.floor(draftToMutate.selectedTile / GLYPHS_PER_BLOCK);
      if (draftToMutate.glyphMode === "128-reverse") {
        draftToMutate.selectedTile &= 0x7f;
      }
      const beforeCount = draftToMutate.tileStyles.length;
      draftToMutate.tileStyles = draftToMutate.tileStyles.filter((style) => style.glyphIndex < imported.glyphCount);
      removedStyles = beforeCount - draftToMutate.tileStyles.length;
      if (selectedStyleId !== null && styleForId(draftToMutate, selectedStyleId) === null) {
        selectedStyleId = null;
        if (brushMode === "tile-style") {
          brushMode = "tile-current-color";
        }
      }
      syncSelectionFromCell(draftToMutate);
    });

    setStatus(
      removedStyles > 0
        ? `Copied ${currentDraft.glyphMode === "128-reverse" ? "128" : "256"} glyphs from the Character Editor into the tile-map tileset and removed ${removedStyles} out-of-range tile style${removedStyles === 1 ? "" : "s"}.`
        : `Copied ${currentDraft.glyphMode === "128-reverse" ? "128" : "256"} glyphs from the Character Editor into the tile-map tileset.`
    );
    renderAll();
  };

  const editTilesetInCharacterEditor = (): void => {
    const currentDraft = draftOrThrow();
    const opened = interop.openTilesetInCharacterEditor({
      activeBlock: currentDraft.activeBlock,
      bytes: cloneBytes(currentDraft.charset.bytes),
      glyphCount: currentDraft.charset.glyphCount,
      sourceLabel: `Imported from Tile Map (${currentDraft.charset.sourceLabel})`
    });
    if (!opened) {
      throw new Error("Character Editor kept its current draft. The tile-map tileset stays available here.");
    }
  };

  const writeScreen = (): void => {
    const currentDraft = draftOrThrow();
    const target = readActiveTextTarget(emulator);
    emulator.writeMemory(target.screenBase, currentDraft.screen);
    setStatus(`Wrote ${SCREEN_SIZE} screen bytes to ${formatHexWordPrefixed(target.screenBase)}.`);
    renderAll();
  };

  const writeColor = (): void => {
    const currentDraft = draftOrThrow();
    const target = readActiveTextTarget(emulator);
    emulator.writeMemory(target.colorBase, currentDraft.color);
    setStatus(`Wrote ${COLOR_SIZE} packed color bytes to ${formatHexWordPrefixed(target.colorBase)}.`);
    renderAll();
  };

  const writeAll = (): void => {
    const currentDraft = draftOrThrow();
    const target = readActiveTextTarget(emulator);
    emulator.writeMemory(target.screenBase, currentDraft.screen);
    emulator.writeMemory(target.colorBase, currentDraft.color);
    emulator.writeMemory(VOC_BG_COLOR_ADDR, new Uint8Array([currentDraft.bgColor & 0x0f]));
    emulator.writeMemory(VOC_BORDER_COLOR_ADDR, new Uint8Array([currentDraft.borderColor & 0x0f]));
    currentDraft.dirty = false;
    setStatus(`Wrote screen, color, BG, and border state for the current tile-map draft.`);
    renderAll();
  };

  const undoDraft = (): void => {
    if (draft === null) {
      return;
    }
    commitPaintStroke();
    const restored = history.undo(draft);
    if (restored === null) {
      return;
    }
    draft = restored;
    pendingRectangleAnchor = null;
    setStatus("Undid the latest tile-map draft change.");
    renderAll();
  };

  const redoDraft = (): void => {
    if (draft === null) {
      return;
    }
    commitPaintStroke();
    const restored = history.redo(draft);
    if (restored === null) {
      return;
    }
    draft = restored;
    pendingRectangleAnchor = null;
    setStatus("Redid the latest tile-map draft change.");
    renderAll();
  };

  const buildHotkeyOptions = (current: number | null): HTMLOptionElement[] => {
    const options: HTMLOptionElement[] = [];
    const none = document.createElement("option");
    none.value = "";
    none.textContent = "Hotkey -";
    options.push(none);
    for (let value = 1; value <= 9; value += 1) {
      const option = document.createElement("option");
      option.value = value.toString();
      option.textContent = `Hotkey ${value}`;
      if (current === value) {
        option.selected = true;
      }
      options.push(option);
    }
    return options;
  };

  const renderStyleList = (currentDraft: TileMapDraft): void => {
    const styles = styleDisplayOrder(currentDraft);
    if (styles.length === 0) {
      const empty = document.createElement("p");
      empty.className = "hint tile-map-style-empty";
      empty.textContent = "No tile styles yet. Save the current brush or create one from the selected cell.";
      elements.styleList.replaceChildren(empty);
      return;
    }

    elements.styleList.replaceChildren(
      ...styles.map((style) => {
        const row = document.createElement("div");
        row.className = "tile-map-style-row";
        if (style.id === selectedStyleId) {
          row.classList.add("is-active");
        }

        const preview = document.createElement("canvas");
        preview.width = 32;
        preview.height = 32;
        preview.className = "tile-map-style-preview";
        renderGlyphPreviewCanvas(preview, currentDraft.charset, currentDraft.palette, currentDraft.bgColor, style.glyphIndex, style.fgColor, style.reverse);

        const body = document.createElement("div");
        body.className = "tile-map-style-body";

        const titleRow = document.createElement("div");
        titleRow.className = "tile-map-style-title-row";
        const title = document.createElement("strong");
        title.textContent = style.name;
        titleRow.append(title);
        if (style.category !== null) {
          const category = document.createElement("span");
          category.className = "tile-map-style-badge";
          category.textContent = style.category;
          titleRow.append(category);
        }
        if (style.reverse) {
          const reverse = document.createElement("span");
          reverse.className = "tile-map-style-badge tile-map-style-badge--reverse";
          reverse.textContent = "REV";
          titleRow.append(reverse);
        }
        if (style.hotkey !== null) {
          const hotkey = document.createElement("span");
          hotkey.className = "tile-map-style-badge tile-map-style-badge--hotkey";
          hotkey.textContent = `#${style.hotkey}`;
          titleRow.append(hotkey);
        }
        body.append(titleRow);

        const detail = document.createElement("p");
        detail.className = "hint tile-map-style-detail";
        detail.textContent = `Glyph ${formatHexWordPrefixed(style.glyphIndex)} • FG ${style.fgColor}`;
        body.append(detail);

        const actions = document.createElement("div");
        actions.className = "tile-map-style-actions";

        const useButton = document.createElement("button");
        useButton.type = "button";
        useButton.textContent = "Use";
        useButton.addEventListener("click", () => {
          try {
            useStyle(style.id);
          } catch (error) {
            setStatus(error instanceof Error ? error.message : String(error));
            renderAll();
          }
        });
        actions.append(useButton);

        const updateButton = document.createElement("button");
        updateButton.type = "button";
        updateButton.textContent = "Update";
        updateButton.addEventListener("click", () => {
          try {
            updateStyleFromBrush(style.id);
          } catch (error) {
            setStatus(error instanceof Error ? error.message : String(error));
            renderAll();
          }
        });
        actions.append(updateButton);

        const renameButton = document.createElement("button");
        renameButton.type = "button";
        renameButton.textContent = "Rename";
        renameButton.addEventListener("click", () => {
          try {
            renameStyle(style.id);
          } catch (error) {
            setStatus(error instanceof Error ? error.message : String(error));
            renderAll();
          }
        });
        actions.append(renameButton);

        const categoryButton = document.createElement("button");
        categoryButton.type = "button";
        categoryButton.textContent = style.category === null ? "Category" : "Category…";
        categoryButton.addEventListener("click", () => {
          try {
            setStyleCategory(style.id);
          } catch (error) {
            setStatus(error instanceof Error ? error.message : String(error));
            renderAll();
          }
        });
        actions.append(categoryButton);

        const deleteButton = document.createElement("button");
        deleteButton.type = "button";
        deleteButton.textContent = "Delete";
        deleteButton.addEventListener("click", () => {
          try {
            deleteStyle(style.id);
          } catch (error) {
            setStatus(error instanceof Error ? error.message : String(error));
            renderAll();
          }
        });
        actions.append(deleteButton);

        const hotkeySelect = document.createElement("select");
        hotkeySelect.className = "field-input tile-map-style-hotkey";
        hotkeySelect.replaceChildren(...buildHotkeyOptions(style.hotkey));
        hotkeySelect.value = style.hotkey === null ? "" : style.hotkey.toString();
        hotkeySelect.addEventListener("change", () => {
          try {
            const nextHotkey = hotkeySelect.value === "" ? null : Number.parseInt(hotkeySelect.value, 10);
            assignStyleHotkey(style.id, Number.isInteger(nextHotkey) ? nextHotkey : null);
          } catch (error) {
            setStatus(error instanceof Error ? error.message : String(error));
            renderAll();
          }
        });
        actions.append(hotkeySelect);

        row.append(preview, body, actions);
        return row;
      })
    );
  };

  const renderQuickStyles = (currentDraft: TileMapDraft): void => {
    const styles = quickStyleOrder(currentDraft);
    if (styles.length === 0) {
      const empty = document.createElement("p");
      empty.className = "hint tile-map-quick-style-empty";
      empty.textContent = "No quick tile styles yet.";
      elements.quickStyles.replaceChildren(empty);
      return;
    }
    elements.quickStyles.replaceChildren(
      ...styles.map((style) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "tile-map-quick-style-chip";
        if (style.id === selectedStyleId) {
          button.classList.add("is-active");
        }
        const preview = document.createElement("canvas");
        preview.width = 24;
        preview.height = 24;
        preview.className = "tile-map-quick-style-chip__preview";
        renderGlyphPreviewCanvas(preview, currentDraft.charset, currentDraft.palette, currentDraft.bgColor, style.glyphIndex, style.fgColor, style.reverse);
        const label = document.createElement("span");
        label.textContent = style.name;
        button.append(preview, label);
        if (style.hotkey !== null) {
          const badge = document.createElement("span");
          badge.className = "tile-map-quick-style-chip__hotkey";
          badge.textContent = String(style.hotkey);
          button.append(badge);
        }
        button.addEventListener("click", () => {
          try {
            useStyle(style.id);
          } catch (error) {
            setStatus(error instanceof Error ? error.message : String(error));
            renderAll();
          }
        });
        return button;
      })
    );
  };

  const renderSuggestedStyles = (currentDraft: TileMapDraft): void => {
    const existing = new Set(currentDraft.tileStyles.map((style) => styleSignature(style.glyphIndex, style.fgColor, style.reverse)));
    suggestedStyles = suggestedStyles.filter((entry) => !existing.has(entry.signature));
    if (suggestedStyles.length === 0) {
      const empty = document.createElement("p");
      empty.className = "hint tile-map-suggestions-empty";
      empty.textContent = "Suggested styles will appear here after analyzing the current map.";
      elements.styleSuggestions.replaceChildren(empty);
      return;
    }
    elements.styleSuggestions.replaceChildren(
      ...suggestedStyles.map((suggestion) => {
        const row = document.createElement("div");
        row.className = "tile-map-suggestion-row";
        const preview = document.createElement("canvas");
        preview.width = 24;
        preview.height = 24;
        preview.className = "tile-map-suggestion-preview";
        renderGlyphPreviewCanvas(
          preview,
          currentDraft.charset,
          currentDraft.palette,
          currentDraft.bgColor,
          suggestion.glyphIndex,
          suggestion.fgColor,
          suggestion.reverse
        );
        const meta = document.createElement("div");
        meta.className = "tile-map-suggestion-meta";
        const title = document.createElement("strong");
        title.textContent = `Glyph ${formatHexWordPrefixed(suggestion.glyphIndex)} • FG ${suggestion.fgColor}`;
        const detail = document.createElement("p");
        detail.className = "hint";
        detail.textContent = `${suggestion.count} cell${suggestion.count === 1 ? "" : "s"}${suggestion.reverse ? " • reverse" : ""}`;
        meta.append(title, detail);
        const addButton = document.createElement("button");
        addButton.type = "button";
        addButton.textContent = "Add";
        addButton.addEventListener("click", () => {
          try {
            addSuggestedStyle(suggestion);
          } catch (error) {
            setStatus(error instanceof Error ? error.message : String(error));
            renderAll();
          }
        });
        row.append(preview, meta, addButton);
        return row;
      })
    );
  };

  const buildMenus = (): WorkbenchMenu[] => [
    {
      id: "file",
      label: "File",
      items: [
        buttonMenuItem("read-active-text-screen", elements.readActiveTrigger),
        buttonMenuItem("new-blank-map", elements.newBlankTrigger),
        menuSeparator(),
        buttonMenuItem("import-project", elements.importProjectTrigger),
        buttonMenuItem("export-project", elements.exportProjectTrigger),
        buttonMenuItem("export-byte", elements.exportByteTrigger)
      ]
    },
    {
      id: "edit",
      label: "Edit",
      items: [
        buttonMenuItem("undo", elements.undoTrigger, { shortcut: "Ctrl/Cmd+Z" }),
        buttonMenuItem("redo", elements.redoTrigger, { shortcut: "Ctrl/Cmd+Shift+Z" })
      ]
    },
    {
      id: "view",
      label: "View",
      items: [
        ...Array.from(elements.zoomSelect.options).map((option) =>
          selectOptionMenuItem(`zoom-${option.value}`, elements.zoomSelect, option.value)
        ),
        menuSeparator(),
        actionMenuItem(
          "toggle-left-panel",
          leftPanelCollapsed ? "Show Tileset Panel" : "Hide Tileset Panel",
          true,
          () => {
            elements.leftRail.click();
          }
        ),
        actionMenuItem(
          "toggle-right-panel",
          rightPanelCollapsed ? "Show Inspector Panel" : "Hide Inspector Panel",
          true,
          () => {
            elements.rightRail.click();
          }
        )
      ]
    },
    {
      id: "tileset",
      label: "Tileset",
      items: [
        buttonMenuItem("use-character-draft", elements.useCharacterDraftTrigger),
        buttonMenuItem("edit-in-characters", elements.editInCharacterEditorTrigger)
      ]
    },
    {
      id: "styles",
      label: "Styles",
      items: [
        buttonMenuItem("save-style", elements.saveStyleTrigger),
        buttonMenuItem("create-style-from-cell", elements.createStyleFromCellTrigger),
        buttonMenuItem("suggest-styles", elements.suggestStylesTrigger),
        menuSeparator(),
        buttonMenuItem("apply-style-selection", elements.applyStyleToSelectionTrigger),
        buttonMenuItem("apply-style-matching", elements.applyStyleToMatchingTrigger)
      ]
    },
    {
      id: "write",
      label: "Write",
      items: [
        buttonMenuItem("write-screen", elements.writeScreenTrigger),
        buttonMenuItem("write-color", elements.writeColorTrigger),
        buttonMenuItem("write-all", elements.writeAllTrigger)
      ]
    }
  ];

  const renderAll = (): void => {
    ensureSelectedStyleIsValid();
    updatePanelRails();
    updateRailOffset();
    renderAtlasCanvas(draft, elements.atlasCanvas);
    if (draft !== null) {
      renderPreviewCanvas(draft, elements.previewCanvas);
    } else {
      const previewContext = elements.previewCanvas.getContext("2d");
      previewContext?.clearRect(0, 0, elements.previewCanvas.width, elements.previewCanvas.height);
    }
    renderEditorCanvas(draft, elements.previewCanvas, elements.editorCanvas);

    for (const tool of tools) {
      tool.button.classList.toggle("is-active", tool.value === currentTool);
      tool.button.disabled = draft === null;
    }

    elements.historyStatus.textContent = historyStateText();
    elements.undoTrigger.disabled = draft === null || !history.getState().canUndo;
    elements.redoTrigger.disabled = draft === null || !history.getState().canRedo;
    elements.importProjectTrigger.disabled = false;
    elements.readActiveTrigger.disabled = false;
    elements.newBlankTrigger.disabled = false;

    if (draft === null) {
      elements.bankMeta.textContent = "-";
      elements.screenBaseMeta.textContent = "-";
      elements.colorBaseMeta.textContent = "-";
      elements.sourceMeta.textContent = "-";
      elements.tilesetSourceMeta.textContent = "-";
      elements.modeMeta.textContent = "-";
      elements.selectedCellMeta.textContent = "-";
      elements.selectedScreenCodeMeta.textContent = "-";
      elements.selectedTileMeta.textContent = "-";
      elements.selectedColorMeta.textContent = "-";
      elements.byteOutput.value = "";
      elements.dirtyIndicator.textContent = "No Draft";
      elements.dirtyIndicator.classList.remove("is-dirty");
      elements.blockSelect.disabled = true;
      elements.fgColorSelect.disabled = true;
      elements.bgColorSelect.disabled = true;
      elements.borderColorSelect.disabled = true;
      elements.reverseToggle.disabled = true;
      elements.useCharacterDraftTrigger.disabled = true;
      elements.editInCharacterEditorTrigger.disabled = true;
      elements.exportProjectTrigger.disabled = true;
      elements.exportByteTrigger.disabled = true;
      elements.brushModeSelect.disabled = true;
      elements.saveStyleTrigger.disabled = true;
      elements.createStyleFromCellTrigger.disabled = true;
      elements.suggestStylesTrigger.disabled = true;
      elements.applyStyleToSelectionTrigger.disabled = true;
      elements.applyStyleToMatchingTrigger.disabled = true;
      elements.quickStyles.replaceChildren();
      elements.styleList.replaceChildren();
      elements.styleSuggestions.replaceChildren();
      elements.writeScreenTrigger.disabled = true;
      elements.writeColorTrigger.disabled = true;
      elements.writeAllTrigger.disabled = true;
      elements.writeHint.textContent = "Create, import, or read a tile-map draft first.";
      refreshMenuBar();
      return;
    }

    let activeTarget: ActiveTextTarget | null = null;
    let activeTargetError: string | null = null;
    try {
      activeTarget = readActiveTextTarget(emulator);
    } catch (error) {
      activeTargetError = error instanceof Error ? error.message : String(error);
    }

    if (activeTarget !== null) {
      elements.bankMeta.textContent = `Bank ${activeTarget.bank} (${formatHexWordPrefixed(activeTarget.bankBase)})`;
      elements.screenBaseMeta.textContent = formatHexWordPrefixed(activeTarget.screenBase);
      elements.colorBaseMeta.textContent = formatHexWordPrefixed(activeTarget.colorBase);
      elements.writeScreenTrigger.disabled = false;
      elements.writeColorTrigger.disabled = false;
      elements.writeAllTrigger.disabled = false;
      elements.writeHint.textContent = `Writeback targets the active text screen at ${formatHexWordPrefixed(activeTarget.screenBase)} and packed Color-RAM at ${formatHexWordPrefixed(activeTarget.colorBase)}. Charset writeback stays in Characters.`;
    } else {
      elements.bankMeta.textContent = "-";
      elements.screenBaseMeta.textContent = "-";
      elements.colorBaseMeta.textContent = "-";
      elements.writeScreenTrigger.disabled = true;
      elements.writeColorTrigger.disabled = true;
      elements.writeAllTrigger.disabled = true;
      elements.writeHint.textContent = activeTargetError ?? "Live text-mode target unavailable.";
    }

    elements.sourceMeta.textContent = draft.sourceLabel;
    elements.tilesetSourceMeta.textContent = draft.charset.sourceLabel;
    elements.modeMeta.textContent = glyphModeLabel(draft.glyphMode);
    elements.selectedCellMeta.textContent = `Cell ${draft.selectedCellX}, ${draft.selectedCellY} / index ${cellIndex(draft.selectedCellX, draft.selectedCellY)}`;
    elements.selectedScreenCodeMeta.textContent = `${formatHexBytePrefixed(draft.screen[cellIndex(draft.selectedCellX, draft.selectedCellY)] ?? 0)} / ${formatAsmByte(draft.screen[cellIndex(draft.selectedCellX, draft.selectedCellY)] ?? 0)}`;
    elements.selectedTileMeta.textContent = `${formatHexWordPrefixed(draft.selectedTile)}${draft.glyphMode === "128-reverse" && draft.selectedReverse ? " + reverse" : ""}`;
    elements.selectedColorMeta.textContent = `Palette ${colorNibbleAt(draft, draft.selectedCellX, draft.selectedCellY)} (${formatAsmByte(colorNibbleAt(draft, draft.selectedCellX, draft.selectedCellY))})`;
    elements.byteOutput.value = buildByteOutput(draft);
    elements.dirtyIndicator.textContent = draft.dirty ? "Dirty" : "Clean";
    elements.dirtyIndicator.classList.toggle("is-dirty", draft.dirty);
    elements.exportProjectTrigger.disabled = false;
    elements.exportByteTrigger.disabled = false;
    elements.brushModeSelect.disabled = false;
    elements.brushModeSelect.value = brushMode;
    elements.saveStyleTrigger.disabled = false;
    elements.createStyleFromCellTrigger.disabled = false;
    elements.suggestStylesTrigger.disabled = false;
    elements.useCharacterDraftTrigger.disabled = false;
    elements.editInCharacterEditorTrigger.disabled = false;
    elements.applyStyleToSelectionTrigger.disabled = selectedStyle() === null;
    elements.applyStyleToMatchingTrigger.disabled = selectedStyle() === null;
    elements.blockSelect.disabled = draft.charset.glyphCount <= GLYPHS_PER_BLOCK;
    setBlockOptions(elements.blockSelect, draft.charset.glyphCount, draft.activeBlock);
    elements.fgColorSelect.disabled = false;
    elements.bgColorSelect.disabled = false;
    elements.borderColorSelect.disabled = false;
    elements.reverseToggle.disabled = draft.glyphMode !== "128-reverse";
    elements.fgColorSelect.value = draft.selectedColor.toString();
    elements.bgColorSelect.value = draft.bgColor.toString();
    elements.borderColorSelect.value = draft.borderColor.toString();
    elements.reverseToggle.checked = draft.selectedReverse;
    renderPaletteSelectBackground(elements.fgColorSelect, draft.palette, draft.selectedColor);
    renderPaletteSelectBackground(elements.bgColorSelect, draft.palette, draft.bgColor);
    renderPaletteSelectBackground(elements.borderColorSelect, draft.palette, draft.borderColor);
    renderQuickStyles(draft);
    renderStyleList(draft);
    renderSuggestedStyles(draft);
    refreshMenuBar();
  };

  elements.readActiveTrigger.addEventListener("click", () => {
    try {
      readActive();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  elements.newBlankTrigger.addEventListener("click", () => {
    try {
      newBlank();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.importProjectTrigger.addEventListener("click", () => {
    elements.importProjectInput.click();
  });

  elements.importProjectInput.addEventListener("change", () => {
    const [file] = Array.from(elements.importProjectInput.files ?? []);
    if (file === undefined) {
      return;
    }
    void importProject(file)
      .catch((error: unknown) => {
        setStatus(error instanceof Error ? error.message : String(error));
        renderAll();
      })
      .finally(() => {
        elements.importProjectInput.value = "";
      });
  });

  elements.exportProjectTrigger.addEventListener("click", () => {
    try {
      exportProject();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.exportByteTrigger.addEventListener("click", () => {
    try {
      exportByte();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.useCharacterDraftTrigger.addEventListener("click", () => {
    try {
      useCharacterDraftAsTileset();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  elements.editInCharacterEditorTrigger.addEventListener("click", () => {
    try {
      editTilesetInCharacterEditor();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  elements.undoTrigger.addEventListener("click", () => {
    undoDraft();
  });

  elements.redoTrigger.addEventListener("click", () => {
    redoDraft();
  });

  elements.blockSelect.addEventListener("change", () => {
    if (draft === null) {
      return;
    }
    const nextBlock = Number.parseInt(elements.blockSelect.value, 10);
    if (!Number.isInteger(nextBlock)) {
      return;
    }
    draft.activeBlock = Math.max(0, Math.min(nextBlock, draft.charset.glyphCount / GLYPHS_PER_BLOCK - 1));
    if (draft.selectedTile < draft.activeBlock * GLYPHS_PER_BLOCK || draft.selectedTile >= (draft.activeBlock + 1) * GLYPHS_PER_BLOCK) {
      draft.selectedTile = draft.activeBlock * GLYPHS_PER_BLOCK;
    }
    renderAll();
  });

  elements.fgColorSelect.addEventListener("change", () => {
    if (draft === null) {
      return;
    }
    const nextColor = Number.parseInt(elements.fgColorSelect.value, 10);
    if (!Number.isInteger(nextColor) || nextColor < 0 || nextColor > 0x0f) {
      return;
    }
    draft.selectedColor = nextColor;
    setStatus(`Brush color changed to palette ${nextColor}.`);
    renderAll();
  });

  elements.bgColorSelect.addEventListener("change", () => {
    try {
      const nextColor = Number.parseInt(elements.bgColorSelect.value, 10);
      if (!Number.isInteger(nextColor) || nextColor < 0 || nextColor > 0x0f) {
        return;
      }
      mutateDraft(`Changed the tile-map background color to palette ${nextColor}.`, "Set tile-map background color", (draftToMutate) => {
        draftToMutate.bgColor = nextColor;
      });
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.borderColorSelect.addEventListener("change", () => {
    try {
      const nextColor = Number.parseInt(elements.borderColorSelect.value, 10);
      if (!Number.isInteger(nextColor) || nextColor < 0 || nextColor > 0x0f) {
        return;
      }
      mutateDraft(`Changed the tile-map border color to palette ${nextColor}.`, "Set tile-map border color", (draftToMutate) => {
        draftToMutate.borderColor = nextColor;
      });
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.reverseToggle.addEventListener("change", () => {
    if (draft === null || draft.glyphMode !== "128-reverse") {
      return;
    }
    draft.selectedReverse = elements.reverseToggle.checked;
    setStatus(draft.selectedReverse ? "Brush reverse mode enabled." : "Brush reverse mode disabled.");
    renderAll();
  });

  elements.zoomSelect.addEventListener("change", () => {
    const nextZoom = Number.parseInt(elements.zoomSelect.value, 10);
    if (nextZoom !== 2 && nextZoom !== 4 && nextZoom !== 8) {
      return;
    }
    if (draft === null) {
      renderAll();
      return;
    }
    draft.zoom = nextZoom;
    setStatus(`Changed tile-map zoom to x${nextZoom}.`);
    renderAll();
  });

  elements.brushModeSelect.addEventListener("change", () => {
    const nextMode = elements.brushModeSelect.value;
    if (nextMode !== "tile-only" && nextMode !== "tile-current-color" && nextMode !== "tile-style") {
      return;
    }
    brushMode = nextMode;
    setStatus(
      nextMode === "tile-only"
        ? "Brush mode changed to Tile only; painting keeps each cell's current foreground color."
        : nextMode === "tile-current-color"
          ? "Brush mode changed to Tile + current color."
          : "Brush mode changed to Tile Style."
    );
    renderAll();
  });

  elements.saveStyleTrigger.addEventListener("click", () => {
    try {
      saveCurrentBrushAsStyle();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  elements.createStyleFromCellTrigger.addEventListener("click", () => {
    try {
      createStyleFromCurrentCell();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  elements.suggestStylesTrigger.addEventListener("click", () => {
    try {
      analyzeSuggestedStyles();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  elements.applyStyleToSelectionTrigger.addEventListener("click", () => {
    try {
      applyStyleToSelection();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  elements.applyStyleToMatchingTrigger.addEventListener("click", () => {
    try {
      applyStyleToMatchingCells();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  elements.leftRail.addEventListener("click", () => {
    leftPanelCollapsed = !leftPanelCollapsed;
    renderAll();
  });

  elements.rightRail.addEventListener("click", () => {
    rightPanelCollapsed = !rightPanelCollapsed;
    renderAll();
  });

  elements.atlasCanvas.addEventListener("click", (event) => {
    if (draft === null) {
      return;
    }
    const glyphIndex = atlasPointToGlyph(event, elements.atlasCanvas, draft.activeBlock, draft.charset.glyphCount);
    if (glyphIndex === null) {
      return;
    }
    draft.selectedTile = glyphIndex;
    draft.activeBlock = Math.floor(glyphIndex / GLYPHS_PER_BLOCK);
    setStatus(`Selected tile ${formatHexWordPrefixed(glyphIndex)} for painting.`);
    renderAll();
  });

  elements.editorCanvas.addEventListener("pointerdown", (event) => {
    if (draft === null) {
      return;
    }
    const point = mapPointToCell(event, elements.editorCanvas);
    if (point === null) {
      return;
    }
    if (event.shiftKey) {
      selectCell(point.x, point.y, `Selected cell ${point.x}, ${point.y} without painting.`);
      return;
    }
    if (currentTool === "fill") {
      applyFillAt(point.x, point.y);
      return;
    }
    if (currentTool === "rectangle") {
      applyRectangleAt(point.x, point.y);
      return;
    }
    applyStampAt(point.x, point.y);
  });

  elements.editorCanvas.addEventListener("pointermove", (event) => {
    if (draft === null || currentTool !== "stamp") {
      return;
    }
    if ((event.buttons & 1) === 0) {
      return;
    }
    const point = mapPointToCell(event, elements.editorCanvas);
    if (point === null) {
      return;
    }
    applyStampAt(point.x, point.y);
  });

  window.addEventListener("pointerup", () => {
    commitPaintStroke();
    renderAll();
  });

  window.addEventListener("resize", () => {
    updateRailOffset();
  });

  elements.writeScreenTrigger.addEventListener("click", () => {
    try {
      writeScreen();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  elements.writeColorTrigger.addEventListener("click", () => {
    try {
      writeColor();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  elements.writeAllTrigger.addEventListener("click", () => {
    try {
      writeAll();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  window.addEventListener("keydown", (event) => {
    if (elements.panel.hidden || isEditableElement(event.target)) {
      return;
    }
    const modifier = event.ctrlKey || event.metaKey;
    if (modifier && event.key.toLowerCase() === "z") {
      event.preventDefault();
      if (event.shiftKey) {
        redoDraft();
        return;
      }
      undoDraft();
      return;
    }
    if (modifier && event.key.toLowerCase() === "y") {
      event.preventDefault();
      redoDraft();
      return;
    }
    if (!modifier && !event.altKey && !event.shiftKey && event.key >= "1" && event.key <= "9") {
      const currentDraft = draft;
      if (currentDraft !== null) {
        const style = currentDraft.tileStyles.find((entry) => entry.hotkey === Number.parseInt(event.key, 10));
        if (style !== undefined) {
          event.preventDefault();
          try {
            useStyle(style.id);
          } catch (error) {
            setStatus(error instanceof Error ? error.message : String(error));
            renderAll();
          }
          return;
        }
      }
    }
    if (event.key === "Escape" && pendingRectangleAnchor !== null) {
      pendingRectangleAnchor = null;
      setStatus("Cancelled the pending rectangle anchor.");
      renderAll();
    }
  });

  menuBar = createWorkbenchMenuBar(menuHost, {
    getMenus: buildMenus,
    label: "Tile Map Editor menu bar"
  });

  setStatus("Open the Tile Map tab or load a draft to inspect and edit text-mode tile maps.");
  updatePanelRails();
  renderAll();

  return {
    refresh() {
      renderAll();
    }
  };
}
